import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import { Warning, Timer } from '@mui/icons-material';

/**
 * Warning dialog shown before auto-logout
 * 
 * @param {boolean} open - Whether dialog is visible
 * @param {number} countdown - Seconds remaining before logout
 * @param {Function} onStayLoggedIn - Handler for "Stay Logged In" button
 * @param {Function} onLogout - Handler for "Logout Now" button
 */
const AutoLogoutWarning = ({ open, countdown, onStayLoggedIn, onLogout }) => {
  const progress = countdown > 0 ? (countdown / 10) * 100 : 0; // 10 second warning

  return (
    <Dialog
      open={open}
      onClose={onStayLoggedIn}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'warning.light',
        color: 'warning.dark'
      }}>
        <Warning />
        <Typography variant="h6" component="span">
          Session Timeout Warning
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Timer sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
          <Typography variant="h4" color="warning.main" gutterBottom>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            seconds remaining
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          You will be automatically logged out due to inactivity.
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Click "Stay Logged In" to continue your session, or "Logout Now" to logout immediately.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color="warning"
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onLogout} 
          variant="outlined"
          color="error"
        >
          Logout Now
        </Button>
        <Button 
          onClick={onStayLoggedIn} 
          variant="contained"
          color="primary"
          autoFocus
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoLogoutWarning;
